import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  SvgIcon,
  TextField
} from "@mui/material";
import { useFormik } from "formik";
import i18n from "i18n";
import MUIDataTable from "mui-datatables";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { AddCircleIcon } from "assets/images";
import CloseFilledIcon from "assets/images/CloseFilled.svg";
import PencilIcon from "assets/images/pencil.svg";
import { CircularLoader, SearchField } from "components/core";
import { useGetRequiredInsertsCountMutation } from "services/api";
import { getMessageByIdFromMessage } from "services/common";
import {
  addIssueContainerOrTransport,
  removeIssueContainerOrTransport,
  selectMediaRequest,
  updateIssueContainerOrTransport
} from "store/slices";
import spacing from "styles/spacing";
import {
  DEFAULT_DATA_TABLE_OPTIONS,
  MEDIA_PROGRAM_TYPES,
  N_CHECKER,
  TRANS,
  Y_CHECKER
} from "utils/constants";
import {
  MESSAGE_10187,
  NOT_APPLICABLE
} from "utils/constants/request-module/MediaRequestDetailConstants";
import { limitFormikTextFieldMaxValue } from "utils/helpers";
import { MediaRequestDetailFormStyles } from "../../media-request-detail-form/MediaRequestDetailForm.Styles";
import { IssueContainersStyles } from "../IssueContainersTab.Styles";
import SelectInsertsModal from "../select-inserts-modal/SelectInsertsModal";

const formInitialValues = {
  id: 0,
  logical_vault_code: 0,
  logical_vault: "",
  media_type_id: 0,
  media_type_descr: "",
  insert_configuration: "",
  quantity: 0,
  have_locks: true,
  lock_type_code: "",
  is_owned: false,
  customer_owned_container_flag: N_CHECKER,
  customer_media_descr_list: "",
  is_managed_container_flag: N_CHECKER
};

// column definition of the permamanent transports data grid view only
const viewPermanentTransportsColumnDefinition = [
  {
    name: "logical_vault_code",
    label: i18n.t("mediaRequestDetail.logicalVaultCaps")
  },
  {
    name: "media_type_descr",
    label: i18n.t("mediaRequestDetail.mediaTypeCaps")
  },
  {
    name: "insert_configuration",
    label: i18n.t("mediaRequestDetail.insertConfigCaps"),
    options: {
      customBodyRender: (value) => (value ? value : NOT_APPLICABLE)
    }
  },
  {
    name: "quantity",
    label: i18n.t("mediaRequestDetail.transportQtyCaps")
  },
  {
    name: "lock_type_code",
    label: i18n.t("mediaRequestDetail.locksCaps"),
    options: {
      customBodyRender: (value) =>
        value
          ? i18n.t("mediaRequestDetail.yes")
          : i18n.t("mediaRequestDetail.no")
    }
  },
  {
    name: "customer_owned_container_flag",
    label: i18n.t("mediaRequestDetail.ownedCaps"),
    options: {
      customBodyRender: (value) =>
        value === Y_CHECKER
          ? i18n.t("mediaRequestDetail.yes")
          : i18n.t("mediaRequestDetail.no")
    }
  }
];

const IssuePermanentTransports = (props) => {
  const { t } = useTranslation();
  const { issueContainers } = useSelector(selectMediaRequest); // select media request slice
  const dispatch = useDispatch(); // redux actions dispatcher
  const [selectedRow, setSelectedRow] = useState(-1);
  const [openInsertConfigModal, setOpenInsertConfigModal] = useState(false);
  const [insertConfigs, setInsertConfigs] = useState([]);
  const message10187 = useMemo(async () => {
    const resp = await getMessageByIdFromMessage({
      message_id: MESSAGE_10187
    });
    return resp;
  }, []); // message 10187

  const [
    getRequiredInsertsCount,
    { data: requiredInsertsCount = 0, reset: resetRequiredInsertsCount }
  ] = useGetRequiredInsertsCountMutation();

  const dataTableOptions = {
    ...DEFAULT_DATA_TABLE_OPTIONS,
    selectableRows: t("mediaRequestDetail.none"),
    jumpToPage: false,
    pagination: false,
    tableBodyHeight: "230px",
    tableBodyMaxHeight: "230px",
    textLabels: {
      body: {
        noMatch: props.isLoading ? (
          <CircularLoader loading={props.isLoading} />
        ) : (
          t("mediaRequestDetail.noItemsToDisplay")
        )
      }
    }
  };

  // set row data to the form
  const handleEditRow = (rowIndex) => {
    const data = issueContainers.containers[rowIndex];
    permanentTransportForm.setValues({
      ...formInitialValues,
      logical_vault_code: data.logical_vault_code,
      logical_vault: data.logical_vault,
      media_type_id: data.media_type_id,
      media_type_descr: data.media_type_descr,
      insert_configuration: data?.insert_configuration,
      quantity: data.quantity,
      have_locks: data?.have_locks,
      is_owned: data?.is_owned,
      customer_owned_container_flag: data?.customer_owned_container_flag
    });
    setInsertConfigs(data.insertConfigs);
    setSelectedRow(rowIndex);
  };

  // column definition of the permamanent transports data grid
  const permanentTransportsColumnDefinition = [
    {
      name: "id",
      label: " ",
      options: {
        customBodyRender: (value, tableMeta) => (
          <IconButton
            id={`btnEdit${value}`}
            aria-label="edit"
            onClick={() => handleEditRow(tableMeta.rowIndex)}
            sx={MediaRequestDetailFormStyles.TableIconButton}
          >
            <img src={PencilIcon} alt="edit icon" />
          </IconButton>
        )
      }
    },
    {
      name: "id",
      label: " ",
      options: {
        customBodyRender: (value, tableMeta) => (
          <IconButton
            id={`btnRemove${value}`}
            aria-label="remove"
            onClick={() =>
              dispatch(removeIssueContainerOrTransport(tableMeta.rowIndex))
            }
            sx={MediaRequestDetailFormStyles.TableIconButton}
          >
            <img src={CloseFilledIcon} alt="close icon" />
          </IconButton>
        )
      }
    },
    {
      name: "logical_vault",
      label: t("mediaRequestDetail.logicalVaultCaps")
    },
    {
      name: "media_type_descr",
      label: t("mediaRequestDetail.mediaTypeCaps")
    },
    {
      name: "insert_configuration",
      label: t("mediaRequestDetail.insertConfigCaps"),
      options: {
        customBodyRender: (value) => (value ? value : NOT_APPLICABLE)
      }
    },
    {
      name: "quantity",
      label: t("mediaRequestDetail.transportQtyCaps")
    },
    {
      name: "have_locks",
      label: t("mediaRequestDetail.locksCaps"),
      options: {
        customBodyRender: (value) =>
          value ? t("mediaRequestDetail.yes") : t("mediaRequestDetail.no")
      }
    },
    {
      name: "customer_owned_container_flag",
      label: t("mediaRequestDetail.ownedCaps"),
      options: {
        customBodyRender: (value) =>
          value === Y_CHECKER
            ? t("mediaRequestDetail.yes")
            : t("mediaRequestDetail.no")
      }
    }
  ];

  /* Add a permanent transport form schema */
  const permanentTransportsValidationSchema = Yup.object().shape({
    logical_vault_code: Yup.number()
      .moreThan(0, t("mediaRequestDetail.selectLogicalVault"))
      .required(t("mediaRequestDetail.commonRequiredError")),
    media_type_id: Yup.number()
      .moreThan(0, t("mediaRequestDetail.selectMediaType"))
      .required(t("mediaRequestDetail.commonRequiredError")),
    insert_configuration: Yup.string().test(
      "insertConfigurationRequired",
      i18n.t("mediaRequestDetail.insertConfigurationRequired"),
      (value) => {
        if (requiredInsertsCount > 0) {
          return value !== undefined && value !== null && value !== "";
        }
        return true;
      }
    ),
    quantity: Yup.number()
      .moreThan(0, message10187[0]?.descr)
      .required(message10187[0]?.descr)
  });

  const permanentTransportForm = useFormik({
    initialValues: formInitialValues,
    validationSchema: permanentTransportsValidationSchema,
    onSubmit: (values, formikHelpers) => {
      if (permanentTransportForm.isValid) {
        const newRow = {
          ...values,
          insertConfigs: insertConfigs,
          mediaIdentifiers: [],
          lock_type_code: values?.have_locks ? TRANS : "",
          customer_owned_container_flag: values?.is_owned
            ? Y_CHECKER
            : N_CHECKER
        };
        if (selectedRow < 0) {
          dispatch(
            addIssueContainerOrTransport({
              ...newRow,
              id: Math.round(Math.random() * 100)
            })
          );
        } else {
          dispatch(
            updateIssueContainerOrTransport({
              index: selectedRow,
              data: newRow
            })
          );
          setSelectedRow(-1);
        }
        setInsertConfigs([]);
        resetRequiredInsertsCount();
        formikHelpers.resetForm();
      }
    }
  });

  // check whether the item requires inserts
  useEffect(() => {
    if (Number(permanentTransportForm.values?.media_type_id) > 0) {
      getRequiredInsertsCount({
        mediaTypeId: permanentTransportForm.values?.media_type_id,
        mediaProgramTypeId: MEDIA_PROGRAM_TYPES.TRANSPORT.ID
      })
        .unwrap()
        .then(async (resp) => {
          if (Number(resp) === 0) {
            await permanentTransportForm.setFieldValue(
              "insert_configuration",
              NOT_APPLICABLE
            );
            setInsertConfigs((prevState) => []);
          }

          if (Number(resp) > 0 && selectedRow < 0) {
            await permanentTransportForm.setFieldValue(
              "insert_configuration",
              ""
            );
            // if the row is new then show the insert configuration modal automatically
            setOpenInsertConfigModal((prevState) => true);
          }
        });
    } else {
      permanentTransportForm.setFieldValue("insert_configuration", "");
      setInsertConfigs((prevState) => []);
      resetRequiredInsertsCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permanentTransportForm.values?.media_type_id]);

  // clear next field values when a field is cleared, fire the dirty event when a value is added
  useEffect(() => {
    if (Number(permanentTransportForm.values?.logical_vault_code) === 0) {
      permanentTransportForm.resetForm();
      props.onFormDirty(false);
    } else {
      props.onFormDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permanentTransportForm.values?.logical_vault_code]);

  // submit form if the tab is changed and the form is dirty
  useEffect(() => {
    if (props.startValidation) {
      permanentTransportForm.submitForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.startValidation]);

  return (
    <>
      {/* Add new container form */}
      <Grid
        id="issuePermanentTransportsGrid1"
        rowSpacing={spacing.verticalMargin20}
        container
        sx={{
          padding: "1rem"
        }}
      >
        <Grid
          id="issuePermanentTransportsGrid2"
          item
          container
          columnSpacing={"1rem"}
          sx={{ marginLeft: "-1rem" }}
        >
          {/* Logical Vault field */}
          <Grid id="issuePermanentTransportsGrid3" item xs={3}>
            <SearchField
              id="issuePermanentTransportsLgicalVault"
              disabled={Boolean(props?.requestId)}
              size="small"
              loading={props.logicalVaultsLoading}
              options={issueContainers.logicalVaults.transport}
              name="logical_vault"
              label={t("mediaRequestDetail.logicalVault")}
              required
              value={permanentTransportForm.values?.logical_vault}
              error={
                permanentTransportForm.touched.logical_vault_code &&
                Boolean(permanentTransportForm.errors.logical_vault_code)
              }
              helperText={
                permanentTransportForm.touched.logical_vault_code &&
                permanentTransportForm.errors.logical_vault_code
              }
              onChange={(_, value) => {
                permanentTransportForm.setFieldValue(
                  "logical_vault_code",
                  value?.value || 0
                );
                permanentTransportForm.setFieldValue(
                  "logical_vault",
                  value?.label || ""
                );
              }}
            />
          </Grid>

          {/* Media Type field */}
          <Grid id="issuePermanentTransportsGrid4" item xs={3}>
            <SearchField
              id="issuePermanentTransportsMediaType"
              disabled={
                Boolean(props?.requestId) ||
                Boolean(permanentTransportForm.values?.logical_vault_code === 0)
              }
              size="small"
              loading={props.mediaTypesLoading}
              options={[...issueContainers.mediaTypes.transport].sort(
                (a, b) => parseInt(a.value) - parseInt(b.value)
              )}
              name="media_type"
              label={t("mediaRequestDetail.mediaType")}
              required
              value={permanentTransportForm.values?.media_type_descr}
              error={
                permanentTransportForm.touched.media_type_id &&
                Boolean(permanentTransportForm.errors.media_type_id)
              }
              helperText={
                permanentTransportForm.touched.media_type_id &&
                permanentTransportForm.errors.media_type_id
              }
              onChange={(_, value) => {
                permanentTransportForm.setFieldValue(
                  "media_type_id",
                  value?.value || 0
                );
                permanentTransportForm.setFieldValue(
                  "media_type_descr",
                  value?.label || ""
                );
              }}
            />
          </Grid>

          {/* Insert Configuration */}
          <Grid id="issuePermanentTransportsGrid5" item xs={3}>
            <TextField
              id="issuePermanentTransportsInsertConfiguration"
              name="insert_configuration"
              disabled
              label={t("mediaRequestDetail.insertConfig")}
              placeholder={t("mediaRequestDetail.insertConfig")}
              value={permanentTransportForm.values?.insert_configuration}
              size="small"
              fullWidth
              required={requiredInsertsCount > 0}
              error={
                permanentTransportForm.touched.insert_configuration &&
                Boolean(permanentTransportForm.errors.insert_configuration)
              }
              helperText={
                permanentTransportForm.touched.insert_configuration &&
                permanentTransportForm.errors.insert_configuration
              }
              InputProps={{
                endAdornment: requiredInsertsCount > 0 && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setOpenInsertConfigModal((prevState) => true)
                      }
                      disabled={Boolean(props?.requestId)}
                    >
                      <SvgIcon viewBox="0 0 24 24">
                        <AddCircleIcon
                          {...IssueContainersStyles.AddCircleIconFill}
                        />
                      </SvgIcon>
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Transport quantity field */}
          <Grid id="issuePermanentTransportsGrid6" item xs={3}>
            <TextField
              id="issuePermanentTransportsTransportQty"
              disabled={
                Boolean(props?.requestId) ||
                Boolean(
                  permanentTransportForm.values?.insert_configuration === ""
                )
              }
              name="quantity"
              type="number"
              label={t("mediaRequestDetail.transportQty")}
              size="small"
              fullWidth
              required
              value={permanentTransportForm.values?.quantity}
              error={
                permanentTransportForm.touched.quantity &&
                Boolean(permanentTransportForm.errors.quantity)
              }
              helperText={
                permanentTransportForm.touched.quantity &&
                permanentTransportForm.errors.quantity
              }
              onChange={(event) =>
                limitFormikTextFieldMaxValue(permanentTransportForm, event, 150)
              }
            />
          </Grid>
        </Grid>
        <Grid
          id="issuePermanentTransportsGrid7"
          item
          container
          columnSpacing={"1rem"}
          sx={{ marginLeft: "-1rem" }}
        >
          {/* Locks */}
          <Grid id="issuePermanentTransportsGrid8" item>
            <FormControlLabel
              id="issuePermanentTransportsLocksLabel"
              disabled={
                Boolean(props?.requestId) ||
                Boolean(
                  permanentTransportForm.values?.insert_configuration === ""
                )
              }
              name="have_locks"
              control={
                <Checkbox
                  id="issuePermanentTransportsLocks"
                  size="small"
                  color="primary"
                />
              }
              label={t("mediaRequestDetail.locks")}
              checked={
                permanentTransportForm.values?.logical_vault?.length > 0 &&
                permanentTransportForm.values?.have_locks
              }
              onChange={permanentTransportForm.handleChange}
            />
          </Grid>

          {/* Owned */}
          <Grid id="issuePermanentTransportsGrid9" item>
            <FormControlLabel
              id="issuePermanentTransportsOwnedLabel"
              disabled={
                Boolean(props?.requestId) ||
                Boolean(
                  permanentTransportForm.values?.insert_configuration === ""
                )
              }
              name="is_owned"
              control={
                <Checkbox
                  id="issuePermanentTransportsOwned"
                  size="small"
                  color="primary"
                />
              }
              label={t("mediaRequestDetail.owned")}
              checked={
                permanentTransportForm.values?.logical_vault?.length > 0 &&
                permanentTransportForm.values?.is_owned
              }
              onChange={permanentTransportForm.handleChange}
            />
          </Grid>

          {/* Add button */}
          <Grid id="issuePermanentTransportsGrid9" item>
            <Button
              id="issue_permanent_transports_add"
              disabled={Boolean(props?.requestId)}
              sx={{ height: "100%" }}
              variant="outlined"
              onClick={() => permanentTransportForm.handleSubmit()}
            >
              {selectedRow < 0
                ? t("mediaRequestDetail.add")
                : t("mediaRequestDetail.update")}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* Containers data grid */}
      <MUIDataTable
        id="issuePermanentTransportsDataTable"
        columns={
          props.requestId
            ? viewPermanentTransportsColumnDefinition
            : permanentTransportsColumnDefinition
        }
        data={issueContainers.containers}
        options={dataTableOptions}
      />

      {/* Insert Configurations modal */}
      {openInsertConfigModal && (
        <SelectInsertsModal
          open={openInsertConfigModal}
          requiredQuantity={Number(requiredInsertsCount)}
          selectedInsertConfigs={insertConfigs}
          onClose={(_) => setOpenInsertConfigModal((prevState) => !prevState)}
          onSubmit={(configs) => {
            setInsertConfigs(configs);
            permanentTransportForm.setFieldValue(
              "insert_configuration",
              configs
                .map((item) => `${item.quantity} - ${item.insert_type}`)
                .toString()
                .replaceAll(",", " ")
            );
            setOpenInsertConfigModal((prevState) => false);
          }}
        />
      )}
    </>
  );
};

IssuePermanentTransports.propTypes = {
  requestId: PropTypes.number, // ID of the media request
  isLoading: PropTypes.bool, // loading transports of the media request
  startValidation: PropTypes.bool, // start form validation or not
  onFormDirty: PropTypes.bool, // if form is dirty event
  logicalVaultsLoading: PropTypes.bool, // Logical vaults loading state
  mediaTypesLoading: PropTypes.bool // Media types loading state
};

export default IssuePermanentTransports;
